import { HttpErrorResponse } from '@angular/common/http';
import { MemoizedSelector } from '@ngrx/store';

import { FormValueFlatten } from '../../../services/form-helper.service';
import { Ga4CategoryInfoSelector, Ga4NavigationDataSelector, Ga4ShopCartDataSelector } from '../store/ga4-analytics.selectors';

import { GA4EcommerceParam, GA4EcommerceValues } from './ecommerce.model';
import {
    GA4EventDataValues,
    GA4ImActionType,
    Ga4ContentType,
    Ga4EventLocation,
    Ga4ImAppStore,
    Ga4ImEventDataCf,
    Ga4ImType,
    Ga4LinkType,
} from './event-data.model';
import { GA4EventType } from './general.model';

import { CartItem, Cart } from '@api/cart/cart.models';
import { Category } from '@core/domain/models/category.models';
import { EcommerceContext } from '@core/domain/models/context.models';
import { CollectionModels } from '@features/cms-collection';
import { ProductPublicModels } from '@features/products/product';
import { ShippingMethod } from '@features/shipping';
import { SidebarCustomComponents } from '@features/sidebar';
import { User } from '@features/user/domain/user.models';
import { FilterEmitter, orderTypes } from '@pages/category/modules/filters/models/product-filter.models';
import { FeelPurchasedCoupon } from '@pages/checkout/summary/domain/models/summary.model';
import { ElementData } from '@shared/directives/element-observer.directive';
import { MenuCategory } from '@shared/header/models/category.model';
import { GridType } from '@shared/models/grid-type.model';
import { ProductViewPlace } from '@shared/product/models';
import {
    RelatedProductType,
} from '@shared/product/related-products/domain/models/related-products.models';

export interface GA4EventConfiguration {
    name: GA4EventType;
    ecommerce: GA4EcommerceParam[] | undefined;
    user?: GA4UserParam[];
    context?: GA4ContextParam[];
    eventData: GA4EventDataParam[] | undefined;
    stage?: 'A+B' | 'C' | 'D' | 'E';
}

export interface paramConfig<t> {
    name: t;
    selector: MemoizedSelector<any, any, any>;
}

export enum GA4UserParam {
    im_user_status = 'im_user_status',
    im_user_type = 'im_user_type',
    im_user_email_sha256_1 = 'im_user_email_sha256_1',
    im_user_email_sha256_2 = 'im_user_email_sha256_2',
    im_user_client_type = 'im_user_client_type',
    im_user_id = 'im_user_id',
    im_store_id = 'im_store_id',
    im_user_client_id = 'im_user_client_id',
}

export enum GA4ContextParam {
    im_language = 'im_language',
    im_local_store_id = 'im_local_store_id',
    im_page_section = 'im_page_section',
    im_page_title = 'im_page_title',
    im_page_type = 'im_page_type',
    im_physical_store = 'im_physical_store',
    im_platform = 'im_platform',
    im_product_family = 'im_product_family',
    im_product_section = 'im_product_section',
    im_product_subfamily = 'im_product_subfamily',
    im_step = 'im_step',
    im_store_status = 'im_store_status',
    im_user_email_sha256 = 'im_user_email_sha256',
    im_user_logged = 'im_user_logged',
    im_user_loyalty = 'im_user_loyalty',
    im_user_profile_status = 'im_user_profile_status',
    im_user_quick_buy = 'im_user_quick_buy',
    page_location = 'page_location',
    page_referrer = 'page_referrer',
    page_title = 'page_title',
    user_id = 'user_id',
}

export enum GA4EventDataParam {
    content_id = 'content_id',
    content_type = 'content_type',
    content_value = 'content_value',
    form_name = 'form_name',
    im_action_type = 'im_action_type',
    im_app_store = 'im_app_store',
    im_bundle_reference = 'im_bundle_reference',
    im_carousel_index = 'im_carousel_index',
    im_category_cross_type = 'im_category_cross_type',
    im_category_view_type = 'im_category_view_type',
    im_collection_reference = 'im_collection_reference',
    im_detail = 'im_detail',
    im_event_data_cf = 'im_event_data_cf',
    im_event_name = 'im_event_name',
    im_item_id = 'im_item_id',
    im_items_number = 'im_items_number',
    im_link_category = 'im_link_category',
    im_link_page_title = 'im_link_page_title',
    im_link_type = 'im_link_type',
    im_location = 'im_location',
    im_order_state = 'im_order_state',
    im_photo_reference = 'im_photo_reference',
    im_photo_type = 'im_photo_type',
    im_physical_store_id = 'im_physical_store_id',
    im_product_color = 'im_product_color',
    im_product_size = 'im_product_size',
    im_transaction_id = 'im_transaction_id',
    im_type = 'im_type',
    item_id = 'item_id',
    link_text = 'link_text',
    link_url = 'link_url',
    method = 'method',
    search_term = 'search_term',
    size_recommender_name = 'size_recommender_name',
    im_shipping_tier = 'im_shipping_tier',
    im_store_zip_code = 'im_store_zip_code',
}

export interface GA4EventDataObjectData {
    categoryProductsIdsLength?: number;
    checkoutAuth?: boolean;
    coupon?: FeelPurchasedCoupon[];
    customCategoryProductsIdsLength?: number;
}

export interface GA4EventObjectData {
    ecommerce: GA4EcommerceValues;
    user: User;
    customContext: CustomContext;
    eventData: GA4EventDataValues;
}

export interface SidebarAnalyticsData {
    data: any;
    name: SidebarCustomComponents;
}

export interface CustomContext {
    cartItems: CartItem[];
    categoryInfo: Ga4CategoryInfoSelector;
    context: EcommerceContext;
    lastSection: string;
    navigationData: Ga4NavigationDataSelector;
    product: ProductPublicModels.PublicProduct;
    shopCartData: Ga4ShopCartDataSelector;
    sidebarLoadedComponent: SidebarAnalyticsData;
    wishListItems: ProductPublicModels.PublicProduct[];
}

export interface CustomEcommerce {
    cart: Cart;
    category: Category;
    products?: ProductPublicModels.PublicProduct[];
    selectedProduct?: ProductPublicModels.PublicProduct;
}

// NOTE: estandar, facebook, google y naver ya contemplados
// Pendiente resto de métodos login
export type LoginMethod =
    'estandar' | 'facebook' | 'google' | 'guest' | 'mobile' | 'naver' | 'wechat';

export interface Ga4EventData {
    baseProduct?: ProductPublicModels.PublicProduct;
    category_view_type?: GridType;
    category?: Category;
    collectionStory?: CollectionModels.CollectionStories;
    contentId?: string;
    contentType?: Ga4ContentType;
    contentValue?: any;
    elementData?: ElementData;
    errorData?: Partial<HttpErrorResponse>;
    errorType?: ErrorType;
    filterEmitter?: FilterEmitter | orderTypes;
    formName?: string;
    formValues?: FormValueFlatten[];
    imActionType?: GA4ImActionType;
    imAppStore?: Ga4ImAppStore;
    imBundleReference?: string;
    imCarouselIndex?: string;
    imDetail?: string;
    dropPointId?: string;
    imEventDataCf?: Ga4ImEventDataCf;
    imEventName?: string;
    imLinkCategory?: string;
    imLinkPageTitle?: string;
    imLinkText?: string;
    imLinkType?: Ga4LinkType;
    imLocation?: Ga4EventLocation;
    imOrderState?: string;
    imPhotoType?: number;
    imTransactionId?: string;
    imType?: Ga4ImType;
    items?: CartItem[] | ProductPublicModels.PublicProduct[];
    linkPageTitle?: string;
    linkTextKey?: string;
    linkType?: string;
    linkUrl?: string;
    mediaPath?: string;
    menuCategory?: MenuCategory;
    method?: LoginMethod;
    shippingMethod?: ShippingMethod;
    newsletterForm?: {
        sections: string[];
    };
    place?: ProductViewPlace;
    relatedProductType?: RelatedProductType;
    relatedsSectionId?: string;
}

export type ErrorType =
    'error_cesta'
    | 'error_checkout' // Pendiente JH
    | 'error_form'
    | 'error_page'
    | 'error_payment_type' // Pendiente JH - duda si va en conjunto con error_server_from
    | 'error_scan' // Pendiente JH
    | 'error_server_form'
    | 'error_servidor_unidadestock'
    | 'error_shipping_tier'
    | 'error_translation';
